import Table from '@/components/table/Table';
import { ButtonCus } from '@/components/common/Button';
import {
    DEFAULT_SELECT_PAGE_SIZES,
    OPTION_FORMAT_CONFIG
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { Option } from '@/types/option';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreateOptionForm from '../create';
import { toJS } from 'mobx';
import TableActionButtons from '@/components/common/TableActionButtons';
import { formatToCurrency } from '@/utils/formatNumber';
import { FORM_TYPE } from '@/types/enums';

export default observer(function OptionManagementPage() {
    // hook
    const { t } = useTranslation();

    // store
    const {
        optionStore: {
            getOptions,
            options,
            paging,
            totalRecord,
            keepSearchParams,
            setObservable,
            clean
        },
        modalStore: { showAlertModal }
    } = useStore();

    // state
    const [rowSelected, setRowSelected] = useState({});

    // lifecycle
    useEffect(() => {
        onSearchOptions(keepSearchParams);
        return () => {
            clean();
        };
    }, []);

    // function
    const onSearchOptions = (searchParams?: any) => {
        onFetchOptions(searchParams);
    };

    const onFetchOptions = (searchParams?: any, paging?: TableState) => {
        getOptions(searchParams, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        setObservable(
            'keepSearchParams',
            toJS({ ...keepSearchParams, paging: newPaging })
        );
        onFetchOptions(keepSearchParams, newPaging);
    };

    const onRefresh = () => {
        onFetchOptions(keepSearchParams, paging);
    };

    // Handle open create option modal
    const handleOpenCreateOption = () => {
        showAlertModal({
            title: t('words_title.option_create'),
            size: '3xl',
            titleClassName: 'text-center max-w-none',
            content: <CreateOptionForm onRefresh={onRefresh} formType={FORM_TYPE.Create} />,
            footerClassName: 'hidden'
        });
    };

    // Handle open create option modal
    const handleOpenEditOption = (optionId: number | string) => {
        showAlertModal({
            title: t('words_title.option_edit'),
            size: '3xl',
            titleClassName: 'text-center max-w-none',
            content: <CreateOptionForm onRefresh={onRefresh} optionId={optionId} formType={FORM_TYPE.Edit} />,
            footerClassName: 'hidden'
        });
    };

    const getOptionNameById = (id: number) => {
        const option = Object.values(OPTION_FORMAT_CONFIG).find(
            item => item.value === id
        );
        return option ? `field.${option.key}` : '';
    };

    // columns
    const columns: ColumnDef<Option>[] = [
        {
            accessorKey: 'actions',
            size: 50,
            enableSorting: false,
            header: '',
            id: 'actions',
            cell: ({ row: { original } }) => (
                <TableActionButtons
                    onView={() => handleOpenEditOption(original.id)}
                />
            )
        },
        {
            accessorKey: 'name',
            header: t('field.option_page.option_name'),
            size: 400,
            enableSorting: false
        },
        {
            accessorKey: 'format',
            header: t('field.format'),
            size: 200,
            enableSorting: false,
            cell: ({ row }) => {
                const value = parseInt(row.getValue('format'));
                return t(getOptionNameById(value));
            }
        },
        {
            accessorKey: 'price',
            header: `${t('field.fee_currency')}`,
            size: 250,
            meta: {
                headerClassName: 'right-sub-header-item pr-6',
                cellClassName: ''
            },
            enableSorting: false,
            cell: ({ row }) => {
                return (
                    <div className='text-right pr-2'>
                        {row.getValue('price') ?
                            `¥${formatToCurrency(row.getValue('price')?.toString())}` :
                            ''}
                    </div>
                );
            }
        }
    ];

    return (
        <div className='layout-card'>
            <div>
                <Table
                    columns={columns}
                    data={options}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    enableRowSelection={false}
                    onRowSelectionChange={setRowSelected}
                    getRowId={row => row.id?.toString()}
                    renderActions={(
                        <ButtonCus
                            variant='default'
                            size='md'
                            onClick={handleOpenCreateOption}
                        >
                            <CirclePlus className='mr-2' />
                            {t('button.add_new')}
                        </ButtonCus>
                    )}
                />
            </div>
        </div>
    );
});
